import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { Link } from '@/components/link/link';
import { config } from '@/config/environment';
import { sanityClient } from '@/content/sanity/sanity-client/sanity-client';
import { PortableText } from '@portabletext/react';

const MainDiv = styled.div`
  height: auto;
  width: 100%;
  padding: 13% 12.5% calc(10.51% - 32px) 12.5%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background: ${({ theme }) => theme.palette.common.midnight};
  @media (max-width: 768px) {
    padding: 98px 20px;
    min-height: 100vh;
  }
`;
const Title = styled(Typography)`
  font-family: var(--font-cooper-light);
  font-style: normal;
  font-weight: 300;
  font-size: 50px;
  line-height: 109.3%;
  color: #ffffff;
  margin-bottom: 50px;
`;
const Question = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: 500;
  font-size: 22px;
  line-height: 160%;
  color: #ffffff;
  margin-bottom: 25.5px;
`;
const Text = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 160%;
  color: #ffffff;
  margin-bottom: 25.5px;
  & a {
    color: #4497aa;
    text-decoration: none;
  }
`;

interface Faq {
  _id: string;
  question: string;
  answer: any[];
}

interface FaqPage {
  title: string;
  questions: Faq[];
}

const Faqs = () => {
  const [faqPage, setFaqPage] = useState<FaqPage | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const archiveMode = config.platformMode === 'archive';

  useEffect(() => {
    const fetchFaqs = async () => {
      const data = await sanityClient.fetch(
        `*[_type=="faqs"][0]{ title, questions[]-> { _id, question, answer } }`,
      );
      setFaqPage(data);
      setLoading(false);
    };

    fetchFaqs();
  }, []);

  if (loading || !faqPage) {
    return <MainDiv></MainDiv>;
  }

  const archiveFaqs = [
    {
      _id: 'who-was-curiko-for',
      question: 'Who was Curiko for?',
      answer:
        'Curiko was for everyone—people with and without disabilities. Anyone could join or host one-on-one and group experiences, and community members could also access coaching and other peer support.',
    },
    {
      _id: 'what-did-curiko-offer',
      question: 'What did Curiko offer?',
      answer:
        'Curiko brought people together through one-on-one and group experiences, both online and in person. Alongside these, community members could access coaching, peer roles, and opportunities to contribute to the community in different ways.',
    },
    {
      _id: 'who-was-behind-curiko',
      question: 'Who was behind Curiko?',
      answer:
        'Curiko was shaped by a team of designers, curators, coaches, a concierge, and experience tasters working alongside volunteer community members. Together, people with and without disabilities co-designed, co-created, and co-delivered the platform, modelling the kinds of reciprocal relationships Curiko hoped to see in the world.',
    },
    {
      _id: 'how-did-people-get-started',
      question: 'How did people get started?',
      answer:
        'Most people began with a Welcome Session, where they met a team member, learned how Curiko worked, and explored ways to participate. From there, they could join online or in-person experiences, host their own, or take on community roles over time.',
    },
    {
      _id: 'did-curiko-only-exist-in-bc',
      question: 'Did Curiko only exist in British Columbia?',
      answer:
        'While in-person experiences took place across British Columbia, people from across Canada—and sometimes beyond—joined online experiences.',
    },
    {
      _id: 'were-all-experiences-online',
      question: 'Were all experiences online?',
      answer:
        'No. Curiko offered both online and in-person experiences, making it easier for people with different interests, schedules, locations, and accessibility needs to participate.',
    },
    {
      _id: 'how-much-did-experiences-cost',
      question: 'How much did experiences cost?',
      answer:
        'Most experiences were offered free of charge. When there were admission fees or material costs, these were noted in advance.',
    },
    {
      _id: 'could-anyone-host-an-experience',
      question: 'Could anyone host an experience?',
      answer:
        "Yes. Anyone could become a host with the support of Curiko's team. Hosting wasn't about having formal expertise—it was about sharing an interest, passion, or curiosity with others.",
    },
    {
      _id: 'what-happened-to-kudoz-comakedo-and-meraki',
      question: 'What happened to Kudoz, CoMakeDo, and Meraki?',
      answer:
        'Curiko grew out of earlier experiments, including Kudoz, CoMakeDo, and Meraki. It brought together the ideas, learnings, and relationships from those projects into one evolving platform.',
    },
    {
      _id: 'why-did-curiko-focus-on-moments',
      question: 'Why did Curiko focus on moments?',
      answer:
        'Curiko was built around the belief that flourishing lives are shaped by everyday moments of connection—to ourselves, to other people, and to the places around us. Rather than focusing only on outcomes, Curiko sought to create opportunities for moments that sparked curiosity, belonging, confidence, and possibility.',
    },
    {
      _id: 'what-did-platform-mean',
      question: 'What did "platform" mean?',
      answer:
        "Curiko wasn't simply a website—it was a platform for participation. It gave people opportunities to share their interests, contribute to the community, build relationships, and shape the culture of the platform together.",
    },
    {
      _id: 'what-did-curiko-offer-families',
      question: 'What did Curiko offer families?',
      answer:
        "Families could explore Curiko's resources, and at times, participate in experiences together. While most community members were adults, some offerings welcomed a broader range of ages depending on the host and activity.",
    },
    {
      _id: 'where-can-i-learn-more',
      question: 'Where can I learn more?',
      answer:
        'Although Curiko is no longer active, this archive shares how the community worked, the values that guided it, and the ideas it explored. To learn more about the thinking behind Curiko or connect with the team at InWithForward, visit the Get Involved page.',
    },
  ];

  const displayedFaqs = archiveMode ? archiveFaqs : faqPage.questions;

  return (
    <MainDiv>
      <Title>{faqPage.title}</Title>
      {displayedFaqs.map(faq => (
        <div key={faq._id}>
          <Question>{faq.question}</Question>
          <Text>
            {archiveMode ? (
              faq.answer
            ) : (
              <PortableText
                value={faq.answer}
                components={{
                  marks: {
                    link: ({ children, value }: any) => (
                      <Link href={value.href.replace('https://', '/').replace('http://', '/')}>
                        {children}
                      </Link>
                    ),
                    strong: ({ children }: any) => <strong>{children}</strong>,
                  },
                  block: {
                    normal: ({ children }: any) => <p>{children}</p>,
                  },
                }}
              />
            )}
          </Text>
        </div>
      ))}
    </MainDiv>
  );
};

export default Faqs;
